import { Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  GraduationCap,
  Mail,
  Phone,
  Search,
  UserX,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PERMISSIONS } from "@/auth/permissions";
import { AgentOnboardingCard } from "@/components/admin/AgentOnboardingCard";
import { ExamDateBadge } from "@/components/admin/ExamDateBadge";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Spinner,
} from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useAgentLicensing,
  useAgentOnboarding,
  useClearAgentExam,
  useSetAgentAuthorizations,
  useSetAgentExam,
} from "@/hooks/useAgents";
import { useHasPermission } from "@/hooks/usePermission";
import type {
  AgentExam,
  AgentLicensing,
  ExamQuestionCount,
} from "@/types/agent";

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

function formatDay(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ============================
// Authorizations
// ============================

function AuthorizationsCard({
  licensing,
  canUpdate,
}: {
  licensing: AgentLicensing;
  canUpdate: boolean;
}) {
  const save = useSetAgentAuthorizations(licensing.agentId);
  const saved = useMemo(
    () =>
      new Set(
        licensing.insurers.filter((i) => i.authorized).map((i) => i.insurerId),
      ),
    [licensing],
  );
  const [selected, setSelected] = useState(saved);
  const [query, setQuery] = useState("");

  // Fresh data from the server (after saving, or someone else's change) resets the form.
  const [syncedWith, setSyncedWith] = useState(licensing);
  if (licensing !== syncedWith) {
    setSyncedWith(licensing);
    setSelected(saved);
  }

  const dirty =
    selected.size !== saved.size || [...selected].some((id) => !saved.has(id));

  const visible = licensing.insurers.filter((i) =>
    i.insurerName.toLowerCase().includes(query.trim().toLowerCase()),
  );

  function toggle(insurerId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(insurerId)) {
        next.delete(insurerId);
      } else {
        next.add(insurerId);
      }
      return next;
    });
  }

  function handleSave() {
    save.mutate([...selected], {
      onSuccess: () => {
        toast.success("Zapisano upoważnienia");
      },
      onError: (error) => {
        toast.error(errorMessage(error, "Nie udało się zapisać upoważnień"));
      },
    });
  }

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-edge-soft px-4 py-3">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-adara-700" aria-hidden />
          <h2 className="text-sm font-semibold text-main">
            Upoważnienia w towarzystwach
          </h2>
          <Badge variant="brand" className="whitespace-nowrap">
            {selected.size} z {licensing.insurers.length}
          </Badge>
        </div>
        {canUpdate && dirty && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelected(saved);
              }}
              disabled={save.isPending}
            >
              Anuluj
            </Button>
            <Button size="sm" onClick={handleSave} isLoading={save.isPending}>
              Zapisz zmiany
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3 p-4">
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder="Szukaj towarzystwa…"
          leftIcon={<Search size={16} aria-hidden />}
          aria-label="Szukaj towarzystwa"
        />
        {visible.length === 0 ? (
          <p className="py-4 text-center text-sm text-faint">
            Nic nie pasuje do wyszukiwania.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {visible.map((insurer) => {
              const checked = selected.has(insurer.insurerId);
              return (
                <li key={insurer.insurerId}>
                  <label
                    className={clsx(
                      "flex h-full items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors",
                      checked
                        ? "border-adara-300 bg-adara-50 dark:border-adara-700 dark:bg-adara-900/20"
                        : "border-edge-soft",
                      canUpdate ? "cursor-pointer hover:bg-subtle" : "",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-adara-700 disabled:cursor-default"
                      checked={checked}
                      disabled={!canUpdate}
                      onChange={() => {
                        toggle(insurer.insurerId);
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-main">
                        {insurer.insurerName}
                        {!insurer.isActive && (
                          <Badge className="ml-2">nieaktywne</Badge>
                        )}
                      </span>
                      {insurer.authorized && insurer.authorizedAt && (
                        <span className="block text-xs text-dim">
                          od {formatDay(insurer.authorizedAt)}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ============================
// Exam
// ============================

const QUESTION_OPTIONS: { value: ExamQuestionCount; label: string }[] = [
  { value: 100, label: "100 pytań" },
  { value: 40, label: "40 pytań" },
];

function ExamForm({
  licensing,
  exam,
  onDone,
}: {
  licensing: AgentLicensing;
  /** The exam being changed; null registers the agent at another insurer. */
  exam: AgentExam | null;
  onDone: () => void;
}) {
  const save = useSetAgentExam(licensing.agentId);
  const [insurerId, setInsurerId] = useState(exam?.insurerId ?? "");
  const [questionCount, setQuestionCount] = useState<ExamQuestionCount>(
    exam?.questionCount ?? 100,
  );
  const [examDate, setExamDate] = useState(exam?.examDate ?? "");

  // Each insurer registers the agent once; the ones already on the list are left out.
  const registered = new Set(licensing.exams.map((e) => e.insurerId));
  const insurerOptions = licensing.insurers
    .filter((i) => i.isActive && !registered.has(i.insurerId))
    .map((i) => ({ value: i.insurerId, label: i.insurerName }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!insurerId) {
      return;
    }
    save.mutate(
      { insurerId, questionCount, examDate: examDate || null },
      {
        onSuccess: () => {
          toast.success("Zapisano zgłoszenie na egzamin");
          onDone();
        },
        onError: (error) => {
          toast.error(errorMessage(error, "Nie udało się zapisać egzaminu"));
        },
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-lg border border-edge-soft p-4"
    >
      {exam ? (
        <p className="text-sm font-semibold text-main">{exam.insurerName}</p>
      ) : (
        <Select
          label="Towarzystwo, które zgłosiło na egzamin"
          options={insurerOptions}
          value={insurerId}
          onChange={setInsurerId}
          placeholder="Wybierz towarzystwo…"
        />
      )}
      <fieldset>
        <legend className="mb-1.5 block text-sm font-medium text-sub">
          Rodzaj egzaminu
        </legend>
        <div className="inline-flex rounded-lg border border-edge-hard p-0.5">
          {QUESTION_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={clsx(
                "cursor-pointer rounded-md px-4 py-1.5 text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-adara-500",
                questionCount === option.value
                  ? "bg-adara-700 text-white"
                  : "text-sub hover:bg-muted",
              )}
            >
              <input
                type="radio"
                name="questionCount"
                className="sr-only"
                checked={questionCount === option.value}
                onChange={() => {
                  setQuestionCount(option.value);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
      <Input
        type="date"
        label="Termin egzaminu"
        hint="Zostaw puste, jeśli termin nie jest jeszcze znany. Dzień przed egzaminem agent i egzaminy@adaraubezpieczenia.pl dostaną przypomnienie e-mailem."
        value={examDate}
        onChange={(e) => {
          setExamDate(e.target.value);
        }}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onDone}
          disabled={save.isPending}
        >
          Anuluj
        </Button>
        <Button type="submit" disabled={!insurerId} isLoading={save.isPending}>
          Zapisz
        </Button>
      </div>
    </form>
  );
}

function ExamRow({
  exam,
  canUpdate,
  disabled,
  onEdit,
  onClear,
  isClearing,
}: {
  exam: AgentExam;
  canUpdate: boolean;
  disabled: boolean;
  onEdit: () => void;
  onClear: () => void;
  isClearing: boolean;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-edge-soft px-4 py-3">
      <dl className="grid min-w-0 flex-1 gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs text-dim">Zgłoszony przez</dt>
          <dd className="mt-1 text-sm font-medium text-main">
            {exam.insurerName}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-dim">Rodzaj</dt>
          <dd className="mt-1 text-sm font-medium text-main">
            {exam.questionCount} pytań
          </dd>
        </div>
        <div>
          <dt className="text-xs text-dim">Termin</dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm font-medium text-main">
            {exam.examDate && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock size={14} aria-hidden />
                {formatDay(exam.examDate)}
              </span>
            )}
            <ExamDateBadge examDate={exam.examDate} />
          </dd>
        </div>
      </dl>
      {canUpdate && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="whitespace-nowrap"
            onClick={onClear}
            disabled={disabled}
            isLoading={isClearing}
            aria-label={`Usuń zgłoszenie — ${exam.insurerName}`}
          >
            Usuń
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            disabled={disabled}
            aria-label={`Edytuj egzamin — ${exam.insurerName}`}
          >
            Edytuj
          </Button>
        </div>
      )}
    </li>
  );
}

function ExamCard({
  licensing,
  canUpdate,
}: {
  licensing: AgentLicensing;
  canUpdate: boolean;
}) {
  const clear = useClearAgentExam(licensing.agentId);
  // The insurer whose exam is being edited, "new" for another registration, null when only listing.
  const [editing, setEditing] = useState<string | null>(null);
  const exams = licensing.exams;
  const canAddMore = licensing.insurers.some(
    (i) => i.isActive && !exams.some((e) => e.insurerId === i.insurerId),
  );

  function handleClear(exam: AgentExam) {
    clear.mutate(exam.insurerId, {
      onSuccess: () => {
        toast.success(`Usunięto zgłoszenie — ${exam.insurerName}`);
      },
      onError: (error) => {
        toast.error(errorMessage(error, "Nie udało się usunąć zgłoszenia"));
      },
    });
  }

  const stopEditing = () => {
    setEditing(null);
  };

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-edge-soft px-4 py-3">
        <div className="flex items-center gap-2">
          <GraduationCap size={18} className="text-adara-700" aria-hidden />
          <h2 className="text-sm font-semibold text-main">
            Egzaminy (nowy agent)
          </h2>
          {exams.length > 0 && (
            <Badge variant="brand" className="whitespace-nowrap">
              {exams.length}
            </Badge>
          )}
        </div>
        {canUpdate && exams.length > 0 && editing === null && canAddMore && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditing("new");
            }}
          >
            Zgłoś w kolejnym TU
          </Button>
        )}
      </div>

      <div className="space-y-3 p-4">
        {exams.length > 0 && (
          <ul className="space-y-2">
            {exams.map((exam) =>
              editing === exam.insurerId ? (
                <li key={exam.insurerId}>
                  <ExamForm
                    licensing={licensing}
                    exam={exam}
                    onDone={stopEditing}
                  />
                </li>
              ) : (
                <ExamRow
                  key={exam.insurerId}
                  exam={exam}
                  canUpdate={canUpdate}
                  disabled={editing !== null || clear.isPending}
                  isClearing={
                    clear.isPending && clear.variables === exam.insurerId
                  }
                  onEdit={() => {
                    setEditing(exam.insurerId);
                  }}
                  onClear={() => {
                    handleClear(exam);
                  }}
                />
              ),
            )}
          </ul>
        )}

        {editing === "new" ? (
          <ExamForm licensing={licensing} exam={null} onDone={stopEditing} />
        ) : (
          exams.length === 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-dim">
                Agent nie jest zgłoszony na egzamin.
              </p>
              {canUpdate && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditing("new");
                  }}
                >
                  Zgłoś na egzamin
                </Button>
              )}
            </div>
          )
        )}
      </div>
    </Card>
  );
}

// ============================
// Page
// ============================

export function AgentDetailPage({ agentId }: { agentId: string }) {
  const { data: licensing, isLoading, isError } = useAgentLicensing(agentId);
  const { data: onboarding } = useAgentOnboarding(agentId);
  const canUpdate = useHasPermission(PERMISSIONS.USERS.UPDATE);

  const backLink = (
    <Link
      to="/agents"
      className="mb-4 inline-flex items-center gap-1.5 text-sm text-dim hover:text-main"
    >
      <ArrowLeft size={16} aria-hidden />
      Agenci
    </Link>
  );

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        {backLink}
        <div className="flex justify-center py-24">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (isError || !licensing) {
    return (
      <div className="animate-fade-in">
        {backLink}
        <Card>
          <EmptyState
            icon={<UserX size={32} />}
            title="Nie znaleziono agenta"
            description="Agent mógł zostać usunięty."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      <div>
        {backLink}
        <PageHeader
          title={`${licensing.firstName} ${licensing.lastName}`}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {licensing.roleName && (
                <Badge variant="brand">{licensing.roleName}</Badge>
              )}
              <Badge variant={licensing.isActive ? "success" : "default"} dot>
                {licensing.isActive ? "Aktywny" : "Wyłączony"}
              </Badge>
            </div>
          }
        />
        <div className="-mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-dim">
          <a
            href={`mailto:${licensing.email}`}
            className="inline-flex items-center gap-1.5 hover:text-main"
          >
            <Mail size={14} aria-hidden />
            {licensing.email}
          </a>
          {licensing.phone && (
            <a
              href={`tel:${licensing.phone}`}
              className="inline-flex items-center gap-1.5 hover:text-main"
            >
              <Phone size={14} aria-hidden />
              {licensing.phone}
            </a>
          )}
          <span>
            Nr RAU:{" "}
            <span className="font-medium text-sub">
              {licensing.rauNumber ?? "—"}
            </span>
          </span>
        </div>
      </div>

      {onboarding && (
        <AgentOnboardingCard onboarding={onboarding} canUpdate={canUpdate} />
      )}
      <ExamCard licensing={licensing} canUpdate={canUpdate} />
      <AuthorizationsCard licensing={licensing} canUpdate={canUpdate} />
    </div>
  );
}
